import React, { useState, useEffect } from 'react';
import { RootStateOrAny, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  InputLabel,
  Select,
  Grid,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  FormControl,
  Checkbox,
} from '@material-ui/core';
import { setSpecificSpecialEventData } from '../../../redux/actions/newOperationHoursActions';
import {
  calculateDeliveryTimeBase,
  canSeeDeliveryRow,
} from '../../../utils/helpers';
import moment from 'moment';
import { formatTime } from '../../../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    root: {
      flexGrow: 1,
      borderBottom: '1px solid',
      borderBottomColor: '#bdbdbd',
      marginTop: 5,
    },
    demo: {
      backgroundColor: '#f5f5f5',
    },
  }),
);

// eslint-disable-next-line import/no-anonymous-default-export
export default function RowDeliveryTime({ disableAll }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const restaurant = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );
  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );

  const { permissions, userInfo } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );

  const driveThruService = restaurantServices.find(
    elem => elem.code === 'driveThru',
  );
  const deliveryService = restaurantServices.find(
    elem => elem.code === 'homeDelivery',
  );

  const {
    deliveryHours,
    deliveryTimeOpenMode,
    deliveryTimeCloseMode,
    deliveryTimeOpen,
    deliveryTimeClose,
    dhIsClosed,
    dhIsClosedMode,
    isCalculated,
    dtTimeOpen,
    dtTimeClose,
    drTimeOpen,
    drTimeClose,
    drIsOpened24Hours,
    dtIsOpened24Hours,
    drIsClosed,
    dtIsClosed,
    availableTimes,
    isEditing,
    is12HFormat,
  } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);

  const [selectOpenSelectedIndex, setSelectOpenSelectedIndex] = useState(0);

  const disabledSelects =
    disableAll || isCalculated === 'Y' || dhIsClosed === 'Y';

  // Only used for getValueSelectOpen and getValueSelectClose functions
  const dtTimeOpenIndex =
    dtIsClosed === 'Y'
      ? ''
      : availableTimes.findIndex(elem => elem === dtTimeOpen);
  const drTimeOpenIndex =
    drIsClosed === 'Y'
      ? ''
      : availableTimes.findIndex(elem => elem === drTimeOpen);
  const indexesOpenArray: number[] = [dtTimeOpenIndex, drTimeOpenIndex].filter(
    elem => elem > 0,
  );
  const minIndex = Math.min(...indexesOpenArray);

  const dtTimeCloseIndex =
    dtIsClosed === 'Y'
      ? ''
      : availableTimes.findIndex(elem => elem === dtTimeClose);
  const drTimeCloseIndex =
    drIsClosed === 'Y'
      ? ''
      : availableTimes.findIndex(elem => elem === drTimeClose);
  const indexesCloseArray: number[] = [
    dtTimeCloseIndex,
    drTimeCloseIndex,
  ].filter(elem => elem > 0);
  //

  const getValueSelectOpen = () => {
    if (dhIsClosed === 'Y') return '';

    if (isCalculated !== 'Y') return deliveryTimeOpen ? deliveryTimeOpen : '';

    if (indexesOpenArray.length) {
      if (!deliveryHours) return availableTimes[minIndex];
      if (
        deliveryHours.brand !== selectedBrand ||
        deliveryHours.country !== restaurant?.CountryId
      )
        return '';
      if (dtIsOpened24Hours !== 'Y' && drIsOpened24Hours !== 'Y')
        return calculateDeliveryTimeBase({
          currentTime: availableTimes[minIndex],
          time: deliveryHours.opening,
          isOpenTime: true,
          minimumOpeningTime: deliveryHours.minimumOpenTime,
        });
      return deliveryHours.default24HourTimeOpen !== null
        ? deliveryHours.default24HourTimeOpen
        : '00:00';
    } else {
      if (dtIsOpened24Hours !== 'Y' && drIsOpened24Hours !== 'Y') return '';
      return deliveryHours.default24HourTimeOpen !== null
        ? deliveryHours.default24HourTimeOpen
        : '00:00';
    }
  };

  const getValueSelectClose = () => {
    if (dhIsClosed === 'Y') return '';

    if (isCalculated !== 'Y') return deliveryTimeClose ? deliveryTimeClose : '';

    if (indexesCloseArray.length) {
      // Determining which index to be considered for delivery time calculation.
      let deliveryCloseTimeIndex = 0;
      let dtCloseTimeOffSet = 0;
      let drCloseTimeOffSet = 0;
      const midNightTime = moment('00:00', 'HH:mm');

      if (
        driveThruService &&
        driveThruService.value &&
        dtTimeCloseIndex &&
        dtTimeOpenIndex &&
        dtTimeCloseIndex !== dtTimeOpenIndex
      ) {
        const dtTimeClose = moment(availableTimes[dtTimeCloseIndex], 'HH:mm');
        const dtTimeOpen = moment(availableTimes[dtTimeOpenIndex], 'HH:mm');
        dtCloseTimeOffSet = dtTimeClose.diff(midNightTime, 'minutes');
        if (dtTimeClose.isBefore(dtTimeOpen)) {
          dtCloseTimeOffSet += 24 * 60;
        }
      }

      if (
        drTimeCloseIndex &&
        drTimeOpenIndex &&
        drTimeCloseIndex !== drTimeOpenIndex
      ) {
        const drTimeClose = moment(availableTimes[drTimeCloseIndex], 'HH:mm');
        const drTimeOpen = moment(availableTimes[drTimeOpenIndex], 'HH:mm');
        drCloseTimeOffSet = drTimeClose.diff(midNightTime, 'minutes');
        if (drTimeClose.isBefore(drTimeOpen)) {
          drCloseTimeOffSet += 24 * 60;
        }
      }
      deliveryCloseTimeIndex =
        dtCloseTimeOffSet >= drCloseTimeOffSet
          ? dtTimeCloseIndex
          : drTimeCloseIndex;

      if (!deliveryHours) return availableTimes[deliveryCloseTimeIndex];

      if (
        deliveryHours.brand !== selectedBrand ||
        deliveryHours.country !== restaurant?.CountryId
      )
        return '';

      if (dtIsOpened24Hours !== 'Y' && drIsOpened24Hours !== 'Y')
        return calculateDeliveryTimeBase({
          currentTime: availableTimes[deliveryCloseTimeIndex],
          time: deliveryHours.closing,
          isOpenTime: false,
        });
      return deliveryHours.default24HourTimeClose !== null
        ? deliveryHours.default24HourTimeClose
        : '23:59';
    }
    if (dtIsOpened24Hours !== 'Y' && drIsOpened24Hours !== 'Y') return '';
    return deliveryHours.default24HourTimeClose !== null
      ? deliveryHours.default24HourTimeClose
      : '23:59';
  };

  const onChangeSelectOpen = event => {
    const select = event.target as HTMLSelectElement;
    if (select.value !== deliveryTimeOpen) {
      dispatch(
        setSpecificSpecialEventData({
          deliveryTimeOpen: select.value,
        }),
      );
    }
  };

  const onChangeSelectClose = event => {
    const select = event.target as HTMLSelectElement;
    if (select.value !== deliveryTimeClose) {
      dispatch(
        setSpecificSpecialEventData({
          deliveryTimeClose: select.value,
        }),
      );
    }
  };

  useEffect(() => {
    if (isCalculated === 'Y') {
      dispatch(
        setSpecificSpecialEventData({
          deliveryTimeOpen: getValueSelectOpen(),
          isEditing: isEditing,
        }),
      );
    }
  }, [
    isCalculated,
    dtTimeOpen,
    drTimeOpen,
    dtIsOpened24Hours,
    drIsOpened24Hours,
    dtIsClosed,
    drIsClosed,
    dhIsClosed,
  ]);

  function getTooltip() {
    if (!canSeeDeliveryRow(permissions, userInfo.role?.code)) {
      return t('operationHours.cantEditDeliveryMessage');
    }
    return isCalculated === 'Y' ? t('operationHours.isCalculatedMessage') : '';
  }
  const tooltip = getTooltip();

  useEffect(() => {
    if (isCalculated === 'Y') {
      dispatch(
        setSpecificSpecialEventData({
          deliveryTimeClose: getValueSelectClose(),
          isEditing: isEditing,
        }),
      );
    }
  }, [
    isCalculated,
    dtTimeClose,
    drTimeClose,
    dtIsOpened24Hours,
    drIsOpened24Hours,
    dtIsClosed,
    drIsClosed,
    dhIsClosed,
  ]);
  const onChangeCheckboxIsClosed = e => {
    const checked = e.target.checked;

    const newState: any = {};

    if (checked) {
      newState.dhIsClosed = 'Y';

      if (dhIsClosed === 'Y' && hasCloseDelivery()) {
        newState.closeToAll = true;
      }
    } else {
      newState.dhIsClosed = 'N';
      newState.closeToAll = false;
    }

    dispatch(setSpecificSpecialEventData(newState));
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={4} md={4}>
          <div className={classes.demo}>
            <List>
              <ListItem>
                <ListItemText primary={t('operationHours.deliveryTime')} />
              </ListItem>
            </List>
          </div>
        </Grid>
        <Grid item xs={1} md={1}>
          <Checkbox style={{ marginTop: 10, visibility: 'hidden' }} />
        </Grid>
        <Grid item xs={1} md={1}>
          <Checkbox
            style={{ marginTop: 10 }}
            disabled={disableAll || !dhIsClosedMode?.includes('write')}
            checked={dhIsClosed === 'Y'}
            onChange={onChangeCheckboxIsClosed}
          />
        </Grid>
        <Grid item xs={3} md={3}>
          {deliveryTimeOpenMode.includes('read') ? (
            <Tooltip title={tooltip}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.start')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectOpen()}
                  label={t('operationHours.start')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectOpen}
                  disabled={disabledSelects}
                >
                  {isCalculated === 'Y' ? (
                    <option value={deliveryTimeOpen ? deliveryTimeOpen : ''}>
                      {deliveryTimeOpen
                        ? formatTime(deliveryTimeOpen, is12HFormat)
                        : ''}
                    </option>
                  ) : (
                    [...availableTimes, getValueSelectOpen()]?.map(
                      (elem, index) => (
                        <option key={index} value={elem}>
                          {formatTime(elem, is12HFormat)}
                        </option>
                      ),
                    )
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
        <Grid item xs={3} md={3}>
          {deliveryTimeCloseMode.includes('read') ? (
            <Tooltip title={tooltip}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.close')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectClose()}
                  label={t('operationHours.close')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectClose}
                  disabled={disabledSelects}
                >
                  {isCalculated === 'Y' ? (
                    <option value={deliveryTimeClose ? deliveryTimeClose : ''}>
                      {deliveryTimeClose
                        ? formatTime(deliveryTimeClose, is12HFormat)
                        : ''}
                    </option>
                  ) : (
                    [...availableTimes, getValueSelectClose()]
                      ?.filter(
                        (elem, index) =>
                          index > selectOpenSelectedIndex || index === 0,
                      )
                      .map((elem, index) => (
                        <option value={elem}>
                          {formatTime(elem, is12HFormat)}
                        </option>
                      ))
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
      </Grid>
    </div>
  );
  function isDeliveryClosed() {
    return dhIsClosed === 'Y';
  }

  function hasDelivery() {
    return deliveryService?.value;
  }

  function hasCloseDelivery() {
    return !hasDelivery() || isDeliveryClosed();
  }
}

RowDeliveryTime.displayName = 'RowDeliveryTime';
